///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Component, computed, DestroyRef, inject, OnInit, output, signal } from '@angular/core';
import { EMPTY, Observable, of, Subject, switchMap, take } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';

import { E2eTestUtils } from '../../../testing/e2e-test-utils';
import { SuiSvgIconComponent, SuiTranslatePipe } from '@siklu/sui-lib';
import { GtthHistoryService } from '../logical-topology/engine/history-service';
import { GtthInfoWindowsService } from '../logical-topology/map-entities-info/info-windows-service';
import { ActionsToolbarModeEnum } from '../../generated/shared-types.model';
import { ActionsToolbarSearchService, GeocodingResults, SearchResults } from './actions-toolbar-search.service';
import { SearchResultStation, SearchResultStations } from './search-station.model';
import { GtthProjectStateService } from '../logical-topology/engine/project-state.service';
import { ContextSensitiveHelpComponent } from '../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { ButtonsDirectivesModule } from '../../shared/directives/buttons-directives/buttons-directives.module';
import { ACTIONS_TOOLBAR_CONSTANTS } from './actions-toolbar.constants';

@Component({
    selector: 'winde-map-actions-toolbar',
    templateUrl: './actions-toolbar.component.html',
    styleUrls: ['./actions-toolbar.scss'],
    imports: [ContextSensitiveHelpComponent, ButtonsDirectivesModule, SuiSvgIconComponent, FormsModule, SuiTranslatePipe, NgClass]
})
export class ActionsToolbarComponent implements OnInit {
    protected readonly modeType = ActionsToolbarModeEnum;

    private readonly destroyRef = inject(DestroyRef);
    private readonly searchService = inject(ActionsToolbarSearchService);
    protected readonly historyService = inject(GtthHistoryService);
    protected readonly infoWindowsService = inject(GtthInfoWindowsService);
    protected readonly projectStateService = inject(GtthProjectStateService);

    protected readonly searchString = signal<string>('');
    protected readonly searchResults = signal<SearchResults>(this.getEmptySearchResults());

    protected readonly preventClose = signal<boolean>(false);
    protected readonly isCommandsInProcess = toSignal(this.historyService.getCurrentCommandsStateSubscription(), { initialValue: false });

    protected readonly hasSuggestions = computed(() => {
        const { locations, stations } = this.searchResults();
        return this.hasLocationSuggestions(locations) || this.hasStationSuggestions(stations);
    });

    protected readonly hasSuggestedStations = computed(() => {
        return this.hasStationSuggestions(this.searchResults().stations);
    });

    protected readonly hasSuggestedLocations = computed(() => {
        return this.hasLocationSuggestions(this.searchResults().locations);
    });

    private readonly firstSuggestedLocation = computed(() => {
        return this.searchResults().locations[0];
    });

    private readonly firstSuggestedStation = computed(() => {
        const stations = this.searchResults().stations;
        return stations.stationsByName[0] || stations.stationsByAssignedName[0] || stations.stationsByIPAddress[0];
    });

    readonly triggerProjectSettingsPanel = output<boolean>();

    private readonly searchString$ = new Subject<string>();

    ngOnInit(): void {
        this.searchString$
            .pipe(
                debounceTime(ACTIONS_TOOLBAR_CONSTANTS.DEBOUNCE_TIMEOUT),
                switchMap(() => this.performSearch()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((results: SearchResults) => this.searchResults.set(results));
    }

    private performSearch(): Observable<SearchResults> {
        const searchString = this.searchString();
        if (!searchString.trim()) {
            this.clearSuggestions();
            return EMPTY;
        }

        return this.searchService.getSearchResults(searchString).pipe(catchError(() => of(this.getEmptySearchResults())));
    }

    triggerSittingsToolbar(state: boolean) {
        this.triggerProjectSettingsPanel.emit(state);
    }

    modeChanged(type: ActionsToolbarModeEnum) {
        this.historyService.modeChanged({ type });
    }

    runUndo() {
        if (!this.infoWindowsService.isWindowOpen && this.historyService.isUndoEnabled() && !this.isCommandsInProcess()) {
            this.historyService.undoCommand().then();
        }
    }

    runRedo() {
        if (!this.infoWindowsService.isWindowOpen && this.historyService.isRedoEnabled() && !this.isCommandsInProcess()) {
            this.historyService.redoCommand().then();
        }
    }

    focusSearchInput() {
        this.searchService.generateAllStationsList();
        this.searchString$.next(this.searchString());
    }

    blurSearchInput() {
        if (!this.preventClose()) {
            this.clearSuggestions();
            this.searchService.clearAllStationsList();
        }
    }

    handleInput(): void {
        this.searchString$.next(this.searchString());
    }

    selectSuggestion() {
        const latLng = this.checkIfCoordinates(this.searchString());
        if (latLng) {
            this.setMarkerByCoordinates(latLng);
            return;
        }

        const station = this.firstSuggestedStation();
        if (station) {
            this.selectStation(station);
            return;
        }

        const location = this.firstSuggestedLocation();
        if (location) {
            this.selectLocation(location.text.text);
        }
    }

    selectStation(station: SearchResultStation) {
        this.searchString.set('');
        this.clearSuggestions();
        this.setMarkerToStation(station);
    }

    selectLocation(address: string) {
        this.searchString.set(address);
        this.clearSuggestions();
        this.geocodeAddress(address)
            .pipe(take(1))
            .subscribe(({ latLng, address }) => {
                this.setMarker(latLng, address);
            });
    }

    private setMarkerByCoordinates(latLng: google.maps.LatLng) {
        this.searchString.set('');
        this.clearSuggestions();
        this.setMarker(latLng);
    }

    private geocodeAddress(address: string): Observable<GeocodingResults> {
        if (!E2eTestUtils.isGeocodingEnabled()) {
            return EMPTY;
        }

        return this.searchService.geocode(address);
    }

    private checkIfCoordinates(searchString: string): google.maps.LatLng {
        const match = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/.exec(searchString.trim());
        if (!match) {
            return null;
        }

        const latLng = match[0].split(',');
        const lat = parseFloat(latLng[0].trim());
        const lng = parseFloat(latLng[1].trim());

        return new google.maps.LatLng(lat, lng);
    }

    private setMarker(latLng: google.maps.LatLng, title = '') {
        const currentZoomLevel = this.historyService.map.getZoom();
        const defaultZoomLevel = ACTIONS_TOOLBAR_CONSTANTS.DEFAULT_ZOOM_LEVEL;
        const lat = latLng.lat();
        const lng = latLng.lng();
        this.historyService.mapSetCenterAndZoom(lat, lng, Math.max(currentZoomLevel, defaultZoomLevel));
        this.historyService.ngZone.runOutsideAngular(() => {
            this.historyService.addSearchMarker({ lat, lng }, title);
        });
    }

    private setMarkerToStation(station: SearchResultStation) {
        const { latLng, name } = station;
        if (!latLng || !name) {
            return;
        }

        const lat = latLng.lat();
        const lng = latLng.lng();
        this.historyService.mapSetCenterAndZoom(lat, lng);
        this.historyService.ngZone.runOutsideAngular(() => {
            this.historyService.addSearchMarker({ lat, lng }, name + ' pointer');
        });
    }

    private hasStationSuggestions(stations: SearchResultStations): boolean {
        return stations.stationsByName.length > 0 || stations.stationsByAssignedName.length > 0 || stations.stationsByIPAddress.length > 0;
    }

    private hasLocationSuggestions(locations: google.maps.places.PlacePrediction[]): boolean {
        return locations.length > 0;
    }

    private clearSuggestions(): void {
        this.searchResults.set(this.getEmptySearchResults());
    }

    private getEmptySearchResults(): SearchResults {
        return {
            stations: {
                stationsByName: [],
                stationsByAssignedName: [],
                stationsByIPAddress: []
            },
            locations: []
        };
    }
}
